/**
 * Prioris — Public landing page.
 *
 * Single-page scroll: Hero → Business case → How it works (spec-driven MA + QC)
 *  → Live modules (snapshots) → Value tables → Pre-loaded jurisdictions
 *  → Demo request form (role-based qualification).
 *
 * Submits the demo form to POST /api/contact reusing the existing schema
 * (name, email, company, phone, structure_type[=role], main_issue[=goal],
 *  urgency, description) so no new backend work is required for lead capture.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {

  Shield, FileCheck, FlaskConical, Gauge, Layers, Activity, Landmark, HardHat,
  CheckCircle2, ArrowRight, ChevronRight, AlertTriangle, Clock, TrendingDown,
  TrendingUp, Users, FileText, Zap, Globe2, Bot, BookOpen, BarChart3,
  CheckSquare, Sparkles, LogIn, Building2, Wrench, ScrollText, Upload,
} from 'lucide-react';
import { getApiBase } from '../lib/apiBase';

const BG = '#06120B';
const ACCENT = '#10b981';
const ACCENT_DIM = 'rgba(16,185,129,0.12)';
const INDIGO = '#6366f1';
const INDIGO_DIM = 'rgba(99,102,241,0.1)';
const CARD = 'rgba(255,255,255,0.03)';
const CARD_B = 'rgba(255,255,255,0.06)';
const TEXT = '#f1f5f9';
const TEXT_MUTED = '#94a3b8';
const TEXT_DIM = '#64748b';

const API = getApiBase();

export const PriorisLanding = () => {
  const nav = useNavigate();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", background: BG, color: TEXT }}>
      <TopNav nav={nav} scrollTo={scrollTo} />
      <Hero scrollTo={scrollTo} />
      <BusinessCase />
      <HowItWorks />
      <Snapshots />
      <LiveExamples />
      <ValueTable />
      <Jurisdictions />
      <WhoFor />
      <Roadmap />
      <DemoForm />
      <Footer />
    </div>
  );
};
export default PriorisLanding;

/* ══════════════════════════════════════════════════════════════════════ */
const TopNav = ({ nav, scrollTo }) => (
  <nav className="sticky top-0 z-40 border-b backdrop-blur-md"
    style={{ background: 'rgba(6,18,11,0.85)', borderColor: CARD_B }}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
      <Link to="/engineering" className="flex items-center gap-3 group" data-testid="dd-logo-home">
        <svg width="32" height="32" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Durable Designs logo">
          {[30, 38, 44, 38, 30].map((w, i) => {
            const y = [6, 14, 22, 30, 38][i];
            return <rect key={i} x="2" y={y} width={w} height="4" rx="1" fill="#22c55e" />;
          })}
        </svg>
        <span className="text-[15px] font-semibold tracking-[0.12em] uppercase whitespace-nowrap"
          style={{ fontFamily: 'Outfit, sans-serif', color: TEXT }}>
          Durable Designs
        </span>
      </Link>
      <div className="hidden md:flex items-center gap-5 text-[12px]" style={{ color: TEXT_MUTED }}>
        <button onClick={() => scrollTo('why')} className="hover:text-white" data-testid="nav-why">Business case</button>
        <button onClick={() => scrollTo('how')} className="hover:text-white" data-testid="nav-how">How it works</button>
        <button onClick={() => scrollTo('snapshots')} className="hover:text-white" data-testid="nav-snaps">Live modules</button>
        <button onClick={() => scrollTo('jurisdictions')} className="hover:text-white" data-testid="nav-jur">Contract Specifications</button>
        <button onClick={() => scrollTo('demo')} className="hover:text-white" data-testid="nav-demo-req">Request demo</button>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={() => scrollTo('demo')}
          className="px-3 py-1.5 rounded-md text-[12px] font-semibold flex items-center gap-1.5"
          style={{ background: ACCENT, color: '#fff' }}
          data-testid="nav-demo-cta">
          Request demo <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  </nav>
);

/* ══════════════════════════════════════════════════════════════════════ */
const Hero = ({ scrollTo }) => (
  <section className="relative pt-20 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden" data-testid="prioris-hero">
    <div className="absolute inset-0 opacity-[0.03]"
      style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)', backgroundSize: '38px 38px' }} />
    <div className="max-w-6xl mx-auto relative z-10">
      <div className="grid lg:grid-cols-5 gap-10 items-center">
        <div className="lg:col-span-3">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium mb-6"
            style={{ background: ACCENT_DIM, color: ACCENT, border: `1px solid rgba(16,185,129,0.25)` }}>
            <Sparkles className="w-3 h-3" /> Spec-driven Materials Approval & QC for major infrastructure
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.04] mb-5">
            Thousands of spec pages.<br />
            <span style={{ color: ACCENT }}>One interactive workflow.</span>
          </h1>
          <p className="text-base sm:text-lg leading-relaxed max-w-xl mb-3" style={{ color: TEXT_MUTED }}>
            Prioris turns normative materials and construction specifications — FHWA FP-24, HK CEDD GS, UK HA Manual of Contract Documents, Portuguese CE EP — into <strong style={{ color: TEXT }}>live compliance rules</strong> that drive Materials Approvals, Inspection & Test Plans and pouring records straight from your site tablet.
          </p>
          <p className="text-sm mb-8" style={{ color: TEXT_DIM }}>
            Fewer specialised engineers on the MA bench. Faster approvals. Zero missed V/NV limits.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={() => scrollTo('demo')}
              className="px-5 py-3 rounded-lg text-[13px] font-semibold flex items-center gap-2"
              style={{ background: ACCENT, color: '#fff' }}
              data-testid="hero-demo-cta">
              Request a 30-min demo <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={() => scrollTo('how')}
              className="px-5 py-3 rounded-lg text-[13px] font-semibold"
              style={{ background: 'transparent', color: TEXT, border: `1px solid ${CARD_B}` }}
              data-testid="hero-how-cta">
              See how it works
            </button>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-7 text-[11px]" style={{ color: TEXT_DIM }}>
            <Chip icon={Shield} label="FP-24 §552 · pre-loaded" />
            <Chip icon={Shield} label="HK CEDD GS Vol. 2 · pre-loaded" />
            <Chip icon={Shield} label="UK MCHW · pre-loaded" />
            <Chip icon={Shield} label="PT CE EP · pre-loaded" />
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="rounded-xl p-4 relative" style={{ background: CARD, border: `1px solid ${CARD_B}` }}>
            <div className="text-[10px] uppercase tracking-[0.12em] font-semibold mb-3" style={{ color: ACCENT }}>Live dashboard</div>
            <div className="grid grid-cols-3 gap-2 text-center mb-3">
              {[
                { v: '552.03', l: 'Active clauses' },
                { v: '7-day', l: 'Strength est.' },
                { v: 'V / NV', l: 'Live flagging' },
              ].map(x => (
                <div key={x.l} className="p-2 rounded" style={{ background: 'rgba(16,185,129,0.08)' }}>
                  <div className="text-sm font-bold" style={{ color: ACCENT }}>{x.v}</div>
                  <div className="text-[9px]" style={{ color: TEXT_MUTED }}>{x.l}</div>
                </div>
              ))}
            </div>
            {[
              { cp: '552-CP1', label: 'Aggregate · Source conformance', st: 'live' },
              { cp: '552-CP2', label: 'Concrete composition · Trial mix', st: 'live' },
              { cp: '552-CP3', label: 'Produced aggregate · Gradation', st: 'live' },
              { cp: '552-CP4', label: 'Pouring records · slump 50mm V', st: 'green' },
              { cp: '552-CP5', label: 'Compressive strength (7→28 d)', st: 'live' },
            ].map(r => (
              <div key={r.cp} className="flex items-center gap-2 py-1.5 border-t first:border-t-0 text-[11px]"
                style={{ borderColor: CARD_B }}>
                <span className="font-mono text-[10px]" style={{ color: ACCENT }}>{r.cp}</span>
                <span className="flex-1 truncate" style={{ color: TEXT }}>{r.label}</span>
                {r.st === 'green' ? <CheckCircle2 className="w-3 h-3" style={{ color: ACCENT }} /> :
                  <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: ACCENT_DIM, color: ACCENT }}>LIVE</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Chip = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-1.5">
    <Icon className="w-3 h-3" style={{ color: ACCENT }} /> {label}
  </div>
);

/* ══════════════════════════════════════════════════════════════════════ */
const BusinessCase = () => {
  const rows = [
    { icon: Clock, k: 'MA cycle time', before: '4–8 hours per submittal · thousands per project', after: '30 min → 1 hour', delta: '≥ 85% faster', color: ACCENT },
    { icon: Users, k: 'Materials engineers per project', before: '2–4 FTE specialised', after: '0.5 FTE generalist', delta: '≈ 75% reduction', color: ACCENT },
    { icon: AlertTriangle, k: 'Non-conformance caught at pour', before: 'Hours–days (paper field forms)', after: 'Live, on first truckload', delta: 'Real-time', color: ACCENT },
    { icon: TrendingDown, k: 'Rejected pours · re-work cost', before: 'Full demolition + re-cast', after: '7-day strength early warning', delta: '40–70% avoided', color: ACCENT },
    { icon: ScrollText, k: 'Spec onboarding', before: '200–800 pages × human read', after: 'Assisted ingestion *', delta: 'Scalable to any spec', color: INDIGO },
    { icon: FileCheck, k: 'Payment withholdings (LDs)', before: 'Frequent — no audit trail', after: 'Traceable V/NV trail · audit log', delta: 'Contract-grade evidence', color: ACCENT },
  ];
  return (
    <section id="why" className="py-20 px-4 sm:px-6 lg:px-8" style={{ background: '#081610' }} data-testid="sec-business">
      <div className="max-w-6xl mx-auto">
        <SectionHead eyebrow="The business case" title={<>Every hour an engineer spends <span style={{ color: ACCENT }}>copying clauses into forms</span> is an hour not spent on engineering.</>}
          sub="Materials Approval (MA) and Inspection & Test Plans (ITP) are the most-duplicated, least-differentiated paperwork on any major project. Prioris treats the normative spec as a computed ruleset — not a PDF — and drives every form, every acceptance check, every pour record straight from those rules." />
        <div className="overflow-x-auto rounded-xl" style={{ border: `1px solid ${CARD_B}` }}>
          <table className="w-full text-[12px]" style={{ borderCollapse: 'separate', borderSpacing: 0, background: CARD }}>
            <thead>
              <tr className="text-left" style={{ color: TEXT_DIM }}>
                <th className="px-4 py-3 font-normal w-10"></th>
                <th className="px-4 py-3 font-normal">Pain point</th>
                <th className="px-4 py-3 font-normal">Status quo</th>
                <th className="px-4 py-3 font-normal" style={{ color: ACCENT }}>With Prioris</th>
                <th className="px-4 py-3 font-normal text-right" style={{ color: ACCENT }}>Delta</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const Icon = r.icon;
                return (
                  <tr key={r.k} style={{ borderTop: `1px solid ${CARD_B}` }}>
                    <td className="px-4 py-3 align-top"><div className="w-7 h-7 rounded flex items-center justify-center" style={{ background: r.color === ACCENT ? ACCENT_DIM : INDIGO_DIM }}><Icon className="w-3.5 h-3.5" style={{ color: r.color }} /></div></td>
                    <td className="px-4 py-3 font-semibold" style={{ color: TEXT }}>{r.k}</td>
                    <td className="px-4 py-3" style={{ color: TEXT_MUTED }}>{r.before}</td>
                    <td className="px-4 py-3" style={{ color: TEXT }}>{r.after}</td>
                    <td className="px-4 py-3 text-right font-semibold" style={{ color: r.color }}>{r.delta}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] mt-3" style={{ color: TEXT_DIM }}>* Spec ingestion workflow currently in development — will accelerate onboarding of new jurisdictions into the rules engine.</p>
      </div>
    </section>
  );
};

/* ══════════════════════════════════════════════════════════════════════ */
const HowItWorks = () => {
  const steps = [
    { n: '01', icon: BookOpen, t: 'Apply the spec', d: 'Pick the jurisdiction and table (e.g. FP-24 §552 Concrete). Prioris loads every clause, test method, acceptance criterion and frequency into the project rules engine.' },
    { n: '02', icon: FileCheck, t: 'Auto-generate Materials Approvals', d: 'The MA form morphs per clause — Total SCM, w/cm, chloride limit, aggregate gradation — all evaluated server-side against the active clause set. The engineer reviews the decision, not the data-entry.' },
    { n: '03', icon: FlaskConical, t: 'Spec-driven ITPs for QC', d: 'The same ruleset exposes a per-checkpoint ITP tree: CP1 aggregate source, CP2 trial mix, CP3 plant QC, CP4 pouring records, CP5 compressive strength. Each opens the matching data-capture surface.' },
    { n: '04', icon: HardHat, t: 'Site technicians fill records in real time', d: 'On tablets at the point of discharge — slump, temperature, air content, sample ID. Values are tinted green / red as the technician types, using the clause\'s exact V/NV limits.' },
    { n: '05', icon: Bot, t: '7-day strength prediction', d: 'As cube data comes back from the lab, the statistical prediction engine flags pours trending below 28-day target. Early warning reduces re-work windows from weeks to days.' },
    { n: '06', icon: Globe2, t: 'Governance across portfolios', d: 'Platform Creator, Project Admin, EoR, RE, Contractor Engineer, Technician, Consultant, Asset Owner — role-scoped RBAC with seat limits and full audit trail. Same governance model across every asset.' },
  ];
  return (
    <section id="how" className="py-20 px-4 sm:px-6 lg:px-8" data-testid="sec-how">
      <div className="max-w-6xl mx-auto">
        <SectionHead eyebrow="How Prioris works" title={<>From <span style={{ color: ACCENT }}>spec clause</span> to <span style={{ color: ACCENT }}>site verdict</span> — one rule engine.</>}
          sub="No configuration screens. No form-builder. The normative spec itself is the configuration." />
        <div className="grid md:grid-cols-2 gap-4">
          {steps.map(s => {
            const Icon = s.icon;
            return (
              <div key={s.n} className="p-5 rounded-xl"
                style={{ background: CARD, border: `1px solid ${CARD_B}` }}>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="text-[10px] font-mono" style={{ color: ACCENT }}>{s.n}</div>
                    <div className="w-9 h-9 rounded-lg mt-1 flex items-center justify-center" style={{ background: ACCENT_DIM }}>
                      <Icon className="w-4 h-4" style={{ color: ACCENT }} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-[13px] font-bold mb-1" style={{ color: TEXT }}>{s.t}</div>
                    <div className="text-[12px] leading-relaxed" style={{ color: TEXT_MUTED }}>{s.d}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ══════════════════════════════════════════════════════════════════════ */
const Snapshots = () => {
  const items = [
    {
      icon: FileCheck, title: 'Materials Approval · FP-24 §552',
      body: <>The submittal drawer auto-computes Total SCM from fly ash + silica fume + slag. Cement content, w/cm, chloride limit, aggregate gradation are all evaluated live against the selected clause. Reject / approve cycles include audit trail for contractual evidence.</>,
      tags: ['Auto-computed fields', 'Cascading Section → Clause', 'EoR approval trail'],
    },
    {
      icon: FlaskConical, title: 'Quality Control · 5 control points',
      body: <>CP1 Aggregate source · CP2 Trial mix · CP3 Plant QC · CP4 Pouring records · CP5 Compressive strength. Each checkpoint surfaces its acceptance criteria, test method and frequency — and opens the corresponding data-capture view.</>,
      tags: ['Spec-driven checkpoints', 'Read-only evidence pages', 'Live data capture'],
    },
    {
      icon: HardHat, title: 'Pouring Records with live V/NV flagging',
      body: <>Technicians log truckload by truckload: arrival time, plate, ticket, slump, temp, air, sample ID, photos. Slump 50 mm turns the cell green; temperature 40°C turns it red — instantly, before they hit Save.</>,
      tags: ['Instant compliance', 'BIM / Aconex / Dalux section ID', 'Delivery & Placement block'],
    },
    {
      icon: Gauge, title: 'Identity Testing · 7-day strength forecast',
      body: <>Strength forecasting engine consumes cube-break data as it lands in the lab. Forecasts likely 28-day performance, flags pours trending non-conforming early enough to isolate the batch.</>,
      tags: ['Strength forecast', 'Statistical acceptance', 'Re-work avoidance'],
    },
    {
      icon: Users, title: 'Materials governance · 7-role RBAC',
      body: <>Platform Creator (Durable Designs), Project Admin (contractor HQ), EoR, Resident Engineer, Contractor Materials Engineer (max 2), Materials Technicians (max 5), Consultants (max 2), Asset Owner. Seat limits + audit log. Invite by email with enforced first-login password change.</>,
      tags: ['Role-scoped actions', 'Project-level seat limits', 'Audit-log evidence'],
    },
    {
      icon: BarChart3, title: 'Portfolio-wide dashboard',
      body: <>Client-side oversight across every asset in the portfolio: open MA submittals, open NV events, pour volume today, upcoming ITP activities. One pane across portfolio, asset, zone, element.</>,
      tags: ['Multi-project', 'Owner/RE view', 'Export-ready'],
    },
  ];
  return (
    <section id="snapshots" className="py-20 px-4 sm:px-6 lg:px-8" style={{ background: '#081610' }} data-testid="sec-snap">
      <div className="max-w-6xl mx-auto">
        <SectionHead eyebrow="Live in the app today" title={<>Real modules. <span style={{ color: ACCENT }}>Not renders.</span></>}
          sub="Everything below is deployed in the current build. Request a demo and we'll walk you through each module on an actual project." />
        <div className="grid md:grid-cols-2 gap-4">
          {items.map(it => {
            const Icon = it.icon;
            return (
              <div key={it.title} className="p-5 rounded-xl" style={{ background: CARD, border: `1px solid ${CARD_B}` }}>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: ACCENT_DIM }}>
                    <Icon className="w-4 h-4" style={{ color: ACCENT }} />
                  </div>
                  <div className="flex-1">
                    <div className="text-[13px] font-bold mb-1.5" style={{ color: TEXT }}>{it.title}</div>
                    <div className="text-[12px] leading-relaxed mb-3" style={{ color: TEXT_MUTED }}>{it.body}</div>
                    <div className="flex flex-wrap gap-1">
                      {it.tags.map(tg => (
                        <span key={tg} className="px-1.5 py-0.5 rounded text-[10px]" style={{ background: 'rgba(255,255,255,0.03)', color: TEXT_DIM, border: `1px solid ${CARD_B}` }}>{tg}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ══════════════════════════════════════════════════════════════════════
   Live examples — FP-24 §552 Class P MA sheet + 7-day strength forecast
   ══════════════════════════════════════════════════════════════════════ */
const LiveExamples = () => (
  <section id="examples" className="py-20 px-4 sm:px-6 lg:px-8" data-testid="sec-examples">
    <div className="max-w-6xl mx-auto">
      <SectionHead
        eyebrow="Live examples"
        title={<>What it <span style={{ color: ACCENT }}>actually looks like</span>.</>}
        sub="Real outputs pulled from the deployed app. Left: a Materials Approval sheet for FHWA FP-24 §552 Class P concrete with live V/NV flagging. Right: a 7-day compressive strength forecast with uncertainty bounds."
      />
      <div className="grid lg:grid-cols-2 gap-5">
        <MASheetExample />
        <StrengthForecastExample />
      </div>
      <p className="text-[11px] mt-4" style={{ color: TEXT_DIM }}>
        Both surfaces are spec-driven: column set, acceptance bands and statistical priors come from the applied FP-24 §552 table — not from hand-coded logic.
      </p>
    </div>
  </section>
);

/* ── FP-24 §552 Class P — Materials Approval sheet ── */
const MASheetExample = () => {
  const Row = ({ k, v, unit, lo, hi, ok = null }) => {
    // Compute verdict: override > computed from [lo, hi]
    let verdict = ok;
    if (verdict === null) {
      const n = Number(v);
      if (!Number.isFinite(n)) verdict = null;
      else if (lo != null && n < lo) verdict = false;
      else if (hi != null && n > hi) verdict = false;
      else verdict = true;
    }
    const col = verdict === true ? ACCENT : verdict === false ? '#ef4444' : TEXT_DIM;
    const bg = verdict === true ? 'rgba(16,185,129,0.08)' : verdict === false ? 'rgba(239,68,68,0.08)' : 'transparent';
    return (
      <tr style={{ background: bg, borderTop: `1px solid ${CARD_B}` }}>
        <td className="px-3 py-1.5" style={{ color: TEXT }}>{k}</td>
        <td className="px-3 py-1.5 text-right font-mono font-semibold" style={{ color: col }}>{v}{unit ? ` ${unit}` : ''}</td>
        <td className="px-3 py-1.5 text-[10px]" style={{ color: TEXT_DIM }}>
          {lo != null && hi != null ? `${lo} – ${hi}` : lo != null ? `≥ ${lo}` : hi != null ? `≤ ${hi}` : '—'}
        </td>
        <td className="px-3 py-1.5 text-right">
          {verdict === true
            ? <CheckCircle2 className="w-3.5 h-3.5 inline" style={{ color: ACCENT }} />
            : verdict === false
              ? <AlertTriangle className="w-3.5 h-3.5 inline" style={{ color: '#ef4444' }} />
              : <span className="text-[10px]" style={{ color: TEXT_DIM }}>—</span>}
        </td>
      </tr>
    );
  };

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: CARD, border: `1px solid ${CARD_B}` }} data-testid="example-ma-sheet">
      <div className="px-4 py-3 border-b flex items-baseline justify-between flex-wrap gap-2" style={{ borderColor: CARD_B }}>
        <div>
          <div className="text-[10px] uppercase tracking-[0.1em] font-bold" style={{ color: ACCENT }}>MA · FHWA FP-24 §552</div>
          <div className="text-[13px] font-bold" style={{ color: TEXT }}>Class P concrete · submittal MA-0041</div>
          <div className="text-[10px]" style={{ color: TEXT_DIM }}>Structural deck · 30 m³ · producer CEMEX · mix ref P35-15F</div>
        </div>
        <span className="px-2 py-0.5 rounded text-[10px] font-bold"
          style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>
          REJECTED · EoR revision requested
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr className="text-left" style={{ color: TEXT_DIM, background: 'rgba(255,255,255,0.03)' }}>
              <th className="px-3 py-2 font-normal">Compliance field</th>
              <th className="px-3 py-2 font-normal text-right">Value</th>
              <th className="px-3 py-2 font-normal">Spec limit</th>
              <th className="px-3 py-2 font-normal text-right">V/NV</th>
            </tr>
          </thead>
          <tbody>
            <Row k="Minimum 28-day strength, f'c" v="35" unit="MPa" lo={35} />
            <Row k="Cement content" v="380" unit="kg/m³" lo={335} />
            <Row k="w/cm ratio" v="0.42" hi={0.44} />
            <Row k="Fly ash (SCM)" v="150" unit="kg/m³" />
            <Row k="Silica fume (SCM)" v="15" unit="kg/m³" />
            <Row k="Slag (SCM)" v="250" unit="kg/m³" />
            <Row k="Total SCM  (auto-sum, % of binder)" v="415 / 795 = 52.2%" hi={50} ok={false} />
            <Row k="Max chloride (Class P · % cement wt.)" v="0.08" hi={0.06} ok={false} />
            <Row k="Air content" v="6.5" unit="%" lo={5} hi={8} />
            <Row k="Coarse aggregate · gradation" v="AASHTO No. 57" ok={true} />
            <Row k="Fine aggregate · FM" v="2.82" lo={2.3} hi={3.1} />
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 border-t flex items-center gap-3 flex-wrap" style={{ borderColor: CARD_B, background: 'rgba(255,255,255,0.02)' }}>
        <span className="text-[10px]" style={{ color: TEXT_DIM }}>9 V · 2 NV · auto-computed Total SCM · EoR comment: <em style={{ color: TEXT_MUTED }}>"Total SCM exceeds 50% cap (AASHTO M 240 type IP/IS). Chloride ion 0.08% over Class P limit of 0.06%. Please re-trial."</em></span>
      </div>
    </div>
  );
};

/* ── 7-day strength forecast with statistical confidence cloud ── */
const StrengthForecastExample = () => {
  // Seven pours — measured 7-day f'c (MPa) + projected 28-day mean + 95% CI
  // via the 7d × 1.38 mean-shift with ±8% σ on the residual.
  const targetFc = 35;
  const data = [
    { p: 'P1', f7: 27.8, mu28: 38.4, lo28: 35.3, hi28: 41.5 },
    { p: 'P2', f7: 29.1, mu28: 40.2, lo28: 36.9, hi28: 43.4 },
    { p: 'P3', f7: 25.9, mu28: 35.7, lo28: 32.8, hi28: 38.7 },
    { p: 'P4', f7: 28.4, mu28: 39.2, lo28: 36.0, hi28: 42.4 },
    { p: 'P5', f7: 23.1, mu28: 31.9, lo28: 29.3, hi28: 34.5 },  // below target
    { p: 'P6', f7: 29.8, mu28: 41.1, lo28: 37.8, hi28: 44.5 },
    { p: 'P7', f7: 27.2, mu28: 37.5, lo28: 34.5, hi28: 40.6 },
  ];

  const verdictOf = (d) => d.mu28 >= targetFc ? 'on-track'
    : d.hi28 >= targetFc ? 'at-risk' : 'below';
  const colorFor = (v) => v === 'below' ? '#ef4444' : v === 'at-risk' ? '#eab308' : ACCENT;
  const enriched = data.map(d => ({ ...d, v: verdictOf(d) }));
  const counts = enriched.reduce((a, p) => (a[p.v] = (a[p.v] || 0) + 1, a), {});

  const yMin = 15, yMax = 50;
  const W = 560, H = 240, padL = 44, padR = 20, padT = 18, padB = 40;
  const innerW = W - padL - padR, innerH = H - padT - padB;
  const xOf = (i) => padL + (innerW * (i + 0.5)) / enriched.length;
  const yOf = (v) => padT + innerH - ((v - yMin) / (yMax - yMin)) * innerH;
  const targetY = yOf(targetFc);

  // Confidence cloud polygon — upper-bound and lower-bound of the 28-day CI for
  // every pour, stitched together via line-to points.
  const upperPts = enriched.map((d, i) => `${xOf(i)},${yOf(d.hi28)}`).join(' ');
  const lowerPtsReversed = [...enriched].reverse().map((d, i) => `${xOf(enriched.length - 1 - i)},${yOf(d.lo28)}`).join(' ');
  const cloudPoly = `${upperPts} ${lowerPtsReversed}`;
  const meanPath = enriched.map((d, i) => `${i === 0 ? 'M' : 'L'} ${xOf(i)} ${yOf(d.mu28)}`).join(' ');

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: CARD, border: `1px solid ${CARD_B}` }} data-testid="example-strength-forecast">
      <div className="px-4 py-3 border-b flex items-baseline justify-between flex-wrap gap-2" style={{ borderColor: CARD_B }}>
        <div>
          <div className="text-[10px] uppercase tracking-[0.1em] font-bold" style={{ color: ACCENT }}>QC · CP5 · Strength forecast</div>
          <div className="text-[13px] font-bold" style={{ color: TEXT }}>Measured 7-day · projected 28-day cloud</div>
          <div className="text-[10px]" style={{ color: TEXT_DIM }}>Mix P35-15F · 7 cube samples · target f'c = 35 MPa</div>
        </div>
        <span className="px-2 py-0.5 rounded text-[10px] font-bold text-right"
          style={{ background: 'rgba(234,179,8,0.15)', color: '#eab308' }}>
          1 pour trending below target<br />
          <span className="text-[9px] font-semibold" style={{ color: '#eab308' }}>
            Early warning email sent to plant manager
          </span>
        </span>
      </div>

      <div className="p-3">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
          <defs>
            <linearGradient id="cloudGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={ACCENT} stopOpacity="0.35" />
              <stop offset="100%" stopColor={ACCENT} stopOpacity="0.08" />
            </linearGradient>
          </defs>

          {/* Gridlines & y-axis */}
          {[20, 25, 30, 35, 40, 45, 50].map(v => (
            <g key={v}>
              <line x1={padL} y1={yOf(v)} x2={W - padR} y2={yOf(v)}
                stroke={CARD_B} strokeDasharray={v === targetFc ? '0' : '2 3'} />
              <text x={padL - 6} y={yOf(v) + 3} textAnchor="end" fontSize="9"
                fill={v === targetFc ? ACCENT : TEXT_DIM}>{v}</text>
            </g>
          ))}
          {/* Target line */}
          <line x1={padL} y1={targetY} x2={W - padR} y2={targetY} stroke={ACCENT} strokeWidth="1.5" />
          <text x={W - padR} y={targetY - 4} textAnchor="end" fontSize="9"
            fill={ACCENT} fontWeight="700">Target f'c = 35 MPa</text>

          {/* 95% confidence cloud (28-day projection envelope) */}
          <polygon points={cloudPoly} fill="url(#cloudGrad)"
            stroke={ACCENT} strokeOpacity="0.25" strokeWidth="1" />
          {/* Mean 28-day projection line */}
          <path d={meanPath} stroke={ACCENT} strokeWidth="1.5" fill="none" strokeDasharray="3 2" opacity="0.8" />

          {/* Measured 7-day points + 28-day mean dot + verdict colour */}
          {enriched.map((d, i) => (
            <g key={d.p}>
              {/* Vertical connector: 7d measured ↗ 28d mean */}
              <line x1={xOf(i)} y1={yOf(d.f7)} x2={xOf(i)} y2={yOf(d.mu28)}
                stroke={colorFor(d.v)} strokeWidth="1" strokeDasharray="1 2" opacity="0.6" />
              {/* 7-day measured dot (solid square) */}
              <rect x={xOf(i) - 3} y={yOf(d.f7) - 3} width="6" height="6"
                fill={colorFor(d.v)} stroke="#fff" strokeWidth="0.5" />
              {/* 28-day mean dot (hollow circle) */}
              <circle cx={xOf(i)} cy={yOf(d.mu28)} r="4"
                fill={BG} stroke={colorFor(d.v)} strokeWidth="1.8" />
              {/* Label below */}
              <text x={xOf(i)} y={H - padB + 14} textAnchor="middle" fontSize="9"
                fill={colorFor(d.v)} fontWeight={d.v === 'below' ? 700 : 400}>{d.p}</text>
            </g>
          ))}

          {/* Axis labels */}
          <text x={12} y={padT + innerH / 2} fontSize="9" fill={TEXT_DIM}
            transform={`rotate(-90 12 ${padT + innerH / 2})`} textAnchor="middle">
            f'c (MPa) · measured 7d □ &amp; projected 28d ○
          </text>
          <text x={padL + innerW / 2} y={H - 4} textAnchor="middle" fontSize="9" fill={TEXT_DIM}>
            Pour ID (Monday cycle · FP-24 §552.10)
          </text>
        </svg>

        <div className="flex flex-wrap gap-3 text-[10px] mt-1 pl-2" style={{ color: TEXT_MUTED }}>
          <LegendSquare color={ACCENT} label="7-day measured (cube break)" />
          <LegendCircle color={ACCENT} label="28-day projected (mean)" />
          <LegendSwatch color={ACCENT} label="95% confidence cloud" />
          <span className="ml-auto" style={{ color: TEXT_DIM }}>Statistical update (probability)</span>
        </div>
        <div className="flex flex-wrap gap-3 text-[10px] mt-1 pl-2" style={{ color: TEXT_MUTED }}>
          <LegendDot color={ACCENT} label={`On track (${counts['on-track'] || 0})`} />
          <LegendDot color="#eab308" label={`At risk (${counts['at-risk'] || 0})`} />
          <LegendDot color="#ef4444" label={`Below target (${counts['below'] || 0})`} />
        </div>
      </div>
      <div className="px-4 py-2 border-t" style={{ borderColor: CARD_B, background: 'rgba(255,255,255,0.02)' }}>
        <div className="text-[10px]" style={{ color: TEXT_MUTED }}>
          <strong style={{ color: TEXT }}>P5</strong> flagged below target at day 7 — the 28-day confidence cloud dips under f'c=35 MPa for the entire CI envelope, triggering an alert to the Resident Engineer <em>21 days before</em> the 28-day cube break.
        </div>
      </div>
    </div>
  );
};

const LegendDot = ({ color, label }) => (
  <span className="inline-flex items-center gap-1">
    <span className="w-2 h-2 rounded-full" style={{ background: color }} /> {label}
  </span>
);
const LegendSquare = ({ color, label }) => (
  <span className="inline-flex items-center gap-1">
    <span className="w-2 h-2" style={{ background: color }} /> {label}
  </span>
);
const LegendCircle = ({ color, label }) => (
  <span className="inline-flex items-center gap-1">
    <span className="w-2 h-2 rounded-full inline-block" style={{ border: `1.5px solid ${color}` }} /> {label}
  </span>
);
const LegendSwatch = ({ color, label }) => (
  <span className="inline-flex items-center gap-1">
    <span className="w-3 h-2 rounded-sm" style={{ background: color, opacity: 0.25 }} /> {label}
  </span>
);

/* ══════════════════════════════════════════════════════════════════════ */
const ValueTable = () => (
  <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="sec-value">
    <div className="max-w-6xl mx-auto">
      <SectionHead eyebrow="Who gains what" title={<>Value <span style={{ color: ACCENT }}>by stakeholder</span>.</>}
        sub="Prioris is adopted at the project level, but the value accrues across the full delivery stack." />
      <div className="overflow-x-auto rounded-xl" style={{ border: `1px solid ${CARD_B}` }}>
        <table className="w-full text-[12px]" style={{ borderCollapse: 'separate', borderSpacing: 0, background: CARD }}>
          <thead>
            <tr className="text-left" style={{ color: TEXT_DIM }}>
              <th className="px-4 py-3 font-normal">Stakeholder</th>
              <th className="px-4 py-3 font-normal">Primary value</th>
              <th className="px-4 py-3 font-normal">Headline KPI</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Asset Owner / Government', 'Contractual evidence of conformance. Audit trail on every MA and pour.', 'Zero-dispute hand-over'],
              ['Engineer of Record', 'Focus on engineering decisions, not spec transcription. Approvals in hours.', '≥ 90% MA cycle-time cut'],
              ['Resident Engineer / PM', 'Live view of site conformance. Instant NV alerts at point of discharge.', 'Real-time NV detection'],
              ['Contractor · Materials Engineer', 'No more manual RAM typing. Lower headcount, same throughput.', '≈ 75% FTE reduction'],
              ['Contractor · Technician', 'Tablet-first pour logs. Green/red typing flags reduce re-work.', '40–70% re-work avoided'],
              ['Testing Consultant', 'View + comment across MA and QC with scoped RBAC.', 'Scoped advisory access'],
            ].map(r => (
              <tr key={r[0]} style={{ borderTop: `1px solid ${CARD_B}` }}>
                <td className="px-4 py-3 font-semibold" style={{ color: TEXT }}>{r[0]}</td>
                <td className="px-4 py-3" style={{ color: TEXT_MUTED }}>{r[1]}</td>
                <td className="px-4 py-3 font-semibold" style={{ color: ACCENT }}>{r[2]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </section>
);

/* ══════════════════════════════════════════════════════════════════════ */
const Jurisdictions = () => {
  const jur = [
    { code: 'FHWA FP-24', name: 'United States · Federal Highways', mat: 'Concrete §552 (MA + ITP live)', flag: 'US' },
    { code: 'HK CEDD GS', name: 'Hong Kong · Civil Engineering & Development', mat: 'Concrete Vol. 2 §16 + Marine §21 (MA live, QC Q2)', flag: 'HK' },
    { code: 'UK MCHW', name: 'United Kingdom · Highways Agency', mat: 'Concrete Vol. 1 §1700 (MA live)', flag: 'UK' },
    { code: 'PT CE EP', name: 'Portugal · Infraestruturas de Portugal', mat: 'Caderno Encargos · Betões (MA live)', flag: 'PT' },
    { code: 'Steel · ASTM / BS', name: 'Coming Q2 — Rebar, plate, bolts', mat: 'Source approval + mill certificate compliance', flag: '—' },
    { code: 'Asphalt · Superpave / HKCEDD', name: 'Coming Q3 — bituminous', mat: 'Mix design + plant QC', flag: '—' },
  ];
  return (
    <section id="jurisdictions" className="py-20 px-4 sm:px-6 lg:px-8" style={{ background: '#081610' }} data-testid="sec-jur">
      <div className="max-w-6xl mx-auto">
        <SectionHead eyebrow="Normative coverage" title={<>Pre-loaded <span style={{ color: ACCENT }}>country specifications</span>.</>}
          sub="The spec library ships with major concrete specifications already modelled as computed rules. New contractual specifications can be onboarded quickly, with additional automation in development." />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {jur.map(j => (
            <div key={j.code} className="p-4 rounded-xl" style={{ background: CARD, border: `1px solid ${CARD_B}` }}>
              <div className="flex items-start gap-2 mb-2">
                <div className="w-8 h-8 rounded flex items-center justify-center text-[10px] font-bold" style={{ background: ACCENT_DIM, color: ACCENT }}>{j.flag}</div>
                <div className="flex-1">
                  <div className="text-[12px] font-bold" style={{ color: TEXT }}>{j.code}</div>
                  <div className="text-[10px]" style={{ color: TEXT_DIM }}>{j.name}</div>
                </div>
              </div>
              <div className="text-[11px]" style={{ color: TEXT_MUTED }}>{j.mat}</div>
            </div>
          ))}
        </div>
        <div className="mt-6 p-4 rounded-xl flex items-start gap-3"
          style={{ background: INDIGO_DIM, border: `1px solid rgba(99,102,241,0.35)` }}>
          <Upload className="w-5 h-5 flex-shrink-0" style={{ color: INDIGO }} />
          <div>
            <div className="text-[12px] font-bold" style={{ color: TEXT }}>Upcoming · Spec ingestion workflow</div>
            <div className="text-[11px] mt-1" style={{ color: TEXT_MUTED }}>
              Prioris is evolving toward faster onboarding of project specifications into the rules engine, including assisted extraction of clauses, test methods, limits and frequencies. Currently in development.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ══════════════════════════════════════════════════════════════════════ */
const WhoFor = () => (
  <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="sec-who">
    <div className="max-w-6xl mx-auto">
      <SectionHead eyebrow="Who it's for" title={<>Built for <span style={{ color: ACCENT }}>major infrastructure projects</span>.</>}
        sub="Highway infrastructure · tunnels · marine works · rail · high-rise. Anywhere the spec matters and the volume is too big for paper-based MA / ITP." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { icon: Landmark, k: 'Government road admins', d: 'Contract-grade evidence of supplier & contractor conformance across every asset in the portfolio.' },
          { icon: Building2, k: 'Main contractors', d: 'Lower materials-engineering headcount. Faster RAM cycles. Transparent site NV resolution.' },
          { icon: Wrench, k: 'Engineers of Record', d: 'Approve on the engineering substance, not the paperwork. All decisions leave an audit trail.' },
          { icon: HardHat, k: 'Resident / Project Managers', d: 'Live conformance view. Intervene on the first truckload, not after a failed 28-day cube.' },
        ].map(x => {
          const Icon = x.icon;
          return (
            <div key={x.k} className="p-4 rounded-xl" style={{ background: CARD, border: `1px solid ${CARD_B}` }}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2" style={{ background: ACCENT_DIM }}><Icon className="w-4 h-4" style={{ color: ACCENT }} /></div>
              <div className="text-[12px] font-bold mb-1" style={{ color: TEXT }}>{x.k}</div>
              <div className="text-[11px] leading-relaxed" style={{ color: TEXT_MUTED }}>{x.d}</div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ══════════════════════════════════════════════════════════════════════ */
const Roadmap = () => (
  <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ background: '#081610' }} data-testid="sec-road">
    <div className="max-w-6xl mx-auto">
      <SectionHead eyebrow="Product roadmap" title={<>What's <span style={{ color: ACCENT }}>next</span>.</>}
        sub="Current momentum — shipped, shipping, planned." />
      <div className="grid md:grid-cols-3 gap-3">
        <RoadBlock color={ACCENT} title="Shipped" items={[
          'FP-24 §552 MA + QC (5 control points)',
          'HK CEDD GS Vol. 2 §16 MA',
          'UK MCHW Vol. 1 §1700 MA',
          'PT CE EP Concrete MA',
          '7-day strength forecast',
          'RBAC (7 roles + audit log)',
          'Resend onboarding emails',
        ]} />
        <RoadBlock color={INDIGO} title="Shipping" items={[
          'HK CEDD §16 QC control points',
          'Spec ingestion workflow (in development)',
          'Steel MA + ITP (ASTM / BS)',
          'Branded email templates',
          'Audit log viewer UI',
          'Multi-project switcher',
        ]} />
        <RoadBlock color={TEXT_DIM} title="Planned" items={[
          'Asphalt MA + QC',
          'AI Review Agent (rebuttals + draft comments)',
          'Sensors & Monitoring module',
          'Reporting engine',
          'Mobile offline capture',
        ]} />
      </div>
    </div>
  </section>
);

const RoadBlock = ({ color, title, items }) => (
  <div className="p-4 rounded-xl" style={{ background: CARD, border: `1px solid ${CARD_B}` }}>
    <div className="flex items-center gap-2 mb-3">
      <div className="w-2 h-2 rounded-full" style={{ background: color }} />
      <div className="text-[12px] font-bold" style={{ color: TEXT }}>{title}</div>
    </div>
    <ul className="space-y-1.5">
      {items.map(x => (
        <li key={x} className="flex items-start gap-1.5 text-[11px]" style={{ color: TEXT_MUTED }}>
          <CheckSquare className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color }} /> {x}
        </li>
      ))}
    </ul>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════ */
const DemoForm = () => {
  const [form, setForm] = useState({
    name: '', email: '', company: '', phone: '',
    role: '', goal: '', urgency: '', description: '', consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);

  const roles = [
    { k: 'asset_owner', l: 'Asset Owner / Client' },
    { k: 'government', l: 'Government / Road Admin' },
    { k: 'contractor', l: 'Contractor (main / sub)' },
    { k: 'resident_engineer', l: 'Resident Engineer / PM' },
    { k: 'engineer_of_record', l: 'Engineer of Record' },
    { k: 'materials_engineer', l: "Contractor's Materials Engineer" },
    { k: 'consultant', l: 'Testing / Materials Consultant' },
    { k: 'academic_other', l: 'Academic / Other' },
  ];
  const goals = [
    { k: 'reduce_ma_time', l: 'Reduce MA cycle time' },
    { k: 'reduce_headcount', l: 'Reduce specialised-engineering headcount' },
    { k: 'real_time_qc', l: 'Real-time site QC / V-NV flagging' },
    { k: 'early_warning', l: 'Early warning on 7-day strength' },
    { k: 'governance', l: 'Materials governance across portfolio' },
    { k: 'spec_ingestion', l: 'Onboard our own project spec' },
    { k: 'general_eval', l: 'General evaluation' },
  ];
  const urgencies = [
    { k: 'immediate', l: 'Immediate (live project)' },
    { k: '1_3m', l: '1–3 months (tender / start-up)' },
    { k: '3_6m', l: '3–6 months' },
    { k: '6_12m', l: '6–12 months' },
    { k: 'just_exploring', l: 'Just exploring' },
  ];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent || !form.email || !form.name) return;
    setSubmitting(true); setErr(null);
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(), email: form.email.trim(),
          company: form.company.trim(), phone: form.phone.trim(),
          structure_type: form.role, main_issue: form.goal,
          urgency: form.urgency,
          description: `PRIORIS DEMO REQUEST\nRole: ${roles.find(r => r.k === form.role)?.l || form.role}\nGoal: ${goals.find(g => g.k === form.goal)?.l || form.goal}\nUrgency: ${urgencies.find(u => u.k === form.urgency)?.l || form.urgency}\n\nNotes:\n${form.description.trim()}`,
          consent_data_processing: form.consent,
          consent_ai_processing: false,
        }),
      });
      if (!res.ok) throw new Error((await res.text()) || 'Submission failed');
      setDone(true);
    } catch (e) { setErr(e.message); }
    setSubmitting(false);
  };

  if (done) {
    return (
      <section id="demo" className="py-20 px-4 sm:px-6 lg:px-8" data-testid="sec-demo">
        <div className="max-w-xl mx-auto text-center p-8 rounded-xl"
          style={{ background: CARD, border: `1px solid rgba(16,185,129,0.35)` }}>
          <CheckCircle2 className="w-10 h-10 mx-auto mb-3" style={{ color: ACCENT }} />
          <h3 className="text-xl font-bold mb-2" style={{ color: TEXT }}>Request received</h3>
          <p className="text-[12px]" style={{ color: TEXT_MUTED }}>
            Thanks, {form.name.split(' ')[0]}. Our team will reach out within one business day with a 30-minute demo slot.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="demo" className="py-20 px-4 sm:px-6 lg:px-8" data-testid="sec-demo">
      <div className="max-w-4xl mx-auto">
        <SectionHead eyebrow="Request a demo" title={<>See Prioris on <span style={{ color: ACCENT }}>your project</span>.</>}
          sub="Tell us a bit about your role and what you're trying to achieve — we'll tailor the 30-minute walkthrough to your context." />
        <form onSubmit={submit} className="p-6 rounded-xl space-y-4"
          style={{ background: CARD, border: `1px solid ${CARD_B}` }}>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input label="Full name *" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} testid="demo-name" required />
            <Input label="Work email *" type="email" value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} testid="demo-email" required />
            <Input label="Company / organisation" value={form.company} onChange={v => setForm(f => ({ ...f, company: v }))} testid="demo-company" />
            <Input label="Phone (optional)" value={form.phone} onChange={v => setForm(f => ({ ...f, phone: v }))} testid="demo-phone" />
          </div>
          <Select label="I am a… *" value={form.role} onChange={v => setForm(f => ({ ...f, role: v }))} options={roles} testid="demo-role" required />
          <Select label="What are you trying to achieve? *" value={form.goal} onChange={v => setForm(f => ({ ...f, goal: v }))} options={goals} testid="demo-goal" required />
          <Select label="When would you want to deploy?" value={form.urgency} onChange={v => setForm(f => ({ ...f, urgency: v }))} options={urgencies} testid="demo-urgency" />
          <div>
            <div className="text-[11px] font-semibold mb-1" style={{ color: TEXT_MUTED }}>Anything else to tailor the demo?</div>
            <textarea rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="e.g. Our project uses a customised FP-24 spec with locally-adapted SCM limits. We'd like to see how Prioris handles that."
              className="w-full rounded-md px-3 py-2 text-[12px]"
              style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${CARD_B}`, color: TEXT }}
              data-testid="demo-notes" />
          </div>
          <label className="flex items-start gap-2 text-[11px]" style={{ color: TEXT_MUTED }}>
            <input type="checkbox" checked={form.consent} onChange={e => setForm(f => ({ ...f, consent: e.target.checked }))}
              className="mt-0.5" data-testid="demo-consent" />
            I agree to Durable Designs processing these details to contact me about Prioris. No marketing list — one-to-one follow-up only.
          </label>
          {err && <div className="text-[11px] px-2 py-1 rounded" style={{ background: 'rgba(239,68,68,0.12)', color: '#ef4444' }}>{err}</div>}
          <button type="submit" disabled={submitting || !form.consent || !form.name || !form.email || !form.role || !form.goal}
            className="w-full py-3 rounded-lg text-[13px] font-bold flex items-center justify-center gap-2 transition"
            style={{ background: (submitting || !form.consent) ? 'rgba(16,185,129,0.35)' : ACCENT, color: '#fff' }}
            data-testid="demo-submit">
            {submitting ? 'Submitting…' : <>Request demo <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </div>
    </section>
  );
};

const Input = ({ label, type = 'text', value, onChange, testid, required }) => (
  <div>
    <div className="text-[11px] font-semibold mb-1" style={{ color: TEXT_MUTED }}>{label}</div>
    <input type={type} value={value} onChange={e => onChange(e.target.value)} required={required}
      className="w-full rounded-md px-3 py-2 text-[12px]"
      style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${CARD_B}`, color: TEXT }}
      data-testid={testid} />
  </div>
);

const Select = ({ label, value, onChange, options, testid, required }) => (
  <div>
    <div className="text-[11px] font-semibold mb-1" style={{ color: TEXT_MUTED }}>{label}</div>
    <select value={value} onChange={e => onChange(e.target.value)} required={required}
      className="w-full rounded-md px-3 py-2 text-[12px]"
      style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${CARD_B}`, color: TEXT }}
      data-testid={testid}>
      <option value="">—</option>
      {options.map(o => <option key={o.k} value={o.k} style={{ background: BG }}>{o.l}</option>)}
    </select>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════ */
const SectionHead = ({ eyebrow, title, sub }) => (
  <div className="mb-8 max-w-3xl">
    <div className="text-[10px] uppercase tracking-[0.12em] font-bold mb-2" style={{ color: ACCENT }}>{eyebrow}</div>
    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight mb-2" style={{ color: TEXT }}>{title}</h2>
    {sub && <p className="text-[13px] leading-relaxed" style={{ color: TEXT_MUTED }}>{sub}</p>}
  </div>
);

/* ══════════════════════════════════════════════════════════════════════ */
const Footer = () => (
  <footer className="border-t py-8 px-4 sm:px-6 lg:px-8" style={{ borderColor: CARD_B }}>
    <div className="max-w-6xl mx-auto text-[11px] flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" style={{ color: TEXT_DIM }}>
      <div>© 2026 Durable Designs · Prioris is a registered mark.</div>
      <div className="flex gap-4">
        <Link to="/legal" style={{ color: TEXT_MUTED }}>Legal</Link>
        <Link to="/privacy" style={{ color: TEXT_MUTED }}>Privacy</Link>
      </div>
    </div>
  </footer>
);
